import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { fetchWithAuth } from '@/lib/fetchWithAuth';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { TermosAceiteModal } from '@/components/termos/TermosAceiteModal';
import { useToast } from '@/hooks/use-toast';
import { Eye, EyeOff, CheckCircle2, XCircle, Loader2 } from 'lucide-react';
import { DEFINIR_SENHA_REDIRECT_KEY, clearSupabaseSessionFromStorage } from '@/lib/utils';

export default function DefinirSenha() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { user } = useAuth();
  
  // Estados do formulário
  const [senha, setSenha] = useState('');
  const [confirmacaoSenha, setConfirmacaoSenha] = useState('');
  const [mostrarSenha, setMostrarSenha] = useState(false);
  const [mostrarConfirmacao, setMostrarConfirmacao] = useState(false);
  
  // Estados de controle
  const [loading, setLoading] = useState(false);
  const [verificandoToken, setVerificandoToken] = useState(true);
  const [tokenValido, setTokenValido] = useState(false);
  const [nomeUsuario, setNomeUsuario] = useState('');

  // Estados de validação
  const [validacoes, setValidacoes] = useState({
    tamanho: false,
    temLetra: false,
    temNumero: false,
    senhasIguais: false
  });
  const [showTermosModal, setShowTermosModal] = useState(false);

  // Verificar token ao carregar (e quando user ficar disponível no primeiro acesso)
  useEffect(() => {
    verificarToken();
  }, [user]);

  // Atualizar validações quando senha mudar
  useEffect(() => {
    atualizarValidacoes();
  }, [senha, confirmacaoSenha]);

  const verificarToken = async () => {
    const tokenHash = searchParams.get('token_hash');
    const type = searchParams.get('type');

    // Primeiro acesso: aguardar user estar disponível antes de decidir (mantém "Verificando link...")
    if (type === 'primeiro_acesso' && !user) {
      return;
    }

    // Verificar se é primeiro acesso (parceiro logado com senha provisória)
    if (type === 'primeiro_acesso' && user) {
      try {
        const res = await fetchWithAuth('/api/parceiros-auth/verificar-status');
        if (res.status === 401) {
          // Sessão inválida/expirada (ex.: já definiu senha) → limpar e ir para login
          clearSupabaseSessionFromStorage();
          window.location.href = '/auth';
          return;
        }
        if (res.ok) {
          const data = await res.json();
          if (data.senha_provisoria === false) {
            await supabase.auth.refreshSession();
            navigate('/dashboard', { replace: true });
            setVerificandoToken(false);
            return;
          }
        }
      } catch {
        // Em caso de erro na API, segue para o formulário
      }
      console.log('🔐 PRIMEIRO ACESSO: Parceiro autenticado precisa definir senha definitiva');
      setTokenValido(true);
      setNomeUsuario(user.user_metadata?.nome || user.email || 'Parceiro');
      setVerificandoToken(false);
      return;
    }

    // Modo desenvolvimento: aceitar qualquer token_hash
    if (process.env.NODE_ENV === 'development' && tokenHash) {
      console.log('🔧 MODO DESENVOLVIMENTO: Usando token_hash como parceiro_id:', tokenHash);
      
      // Verificar se o parceiro existe
      try {
        const { data: { session } } = await supabase.auth.getSession();
        
        if (!session) {
          // Criar uma sessão temporária para o parceiro
          console.log('🔧 Criando sessão temporária para desenvolvimento');
          
          // Buscar dados do parceiro pelo ID
          const response = await fetch(`/api/parceiros-auth/diagnostico/temp@dev.com`, {
            headers: {
              'Authorization': `Bearer ${process.env.VITE_SUPABASE_ANON_KEY || ''}`
            }
          });
          
          setTokenValido(true);
          setNomeUsuario('Parceiro (Modo Desenvolvimento)');
          setVerificandoToken(false);
          return;
        }
      } catch (error) {
        console.error('Erro no modo desenvolvimento:', error);
      }
      
      setTokenValido(true);
      setNomeUsuario('Parceiro (Modo Desenvolvimento)');
      setVerificandoToken(false);
      return;
    }

    // Modo produção: validação normal
    if (!tokenHash || type !== 'invite') {
      toast({
        title: "❌ Link inválido",
        description: "O link de ativação é inválido ou expirou.",
        variant: "destructive"
      });
      setVerificandoToken(false);
      return;
    }

    try {
      console.log('🔍 Verificando token de convite...');

      // Verificar token e criar sessão temporária
      const { data, error } = await supabase.auth.verifyOtp({
        token_hash: tokenHash,
        type: 'invite'
      });

      if (error) {
        console.error('❌ Erro ao verificar token:', error);
        toast({
          title: "⏰ Link expirado",
          description: "O link de ativação expirou. Solicite um novo convite.",
          variant: "destructive"
        });
        setTokenValido(false);
      } else {
        console.log('✅ Token válido, sessão criada');
        setTokenValido(true);
        
        // Buscar nome do usuário
        if (data.user?.user_metadata?.nome) {
          setNomeUsuario(data.user.user_metadata.nome);
        }
      }
    } catch (error) {
      console.error('💥 Erro inesperado ao verificar token:', error);
      setTokenValido(false);
    } finally {
      setVerificandoToken(false);
    }
  };

  const atualizarValidacoes = () => {
    setValidacoes({
      tamanho: senha.length >= 6,
      temLetra: /[A-Za-z]/.test(senha),
      temNumero: /\d/.test(senha),
      senhasIguais: senha !== '' && senha === confirmacaoSenha
    });
  };

  const senhaValida = () => {
    return validacoes.tamanho && 
           validacoes.temLetra && 
           validacoes.temNumero && 
           validacoes.senhasIguais;
  };

  const executarDefinirSenha = useCallback(async () => {
    setLoading(true);
    try {
      console.log('🔐 Definindo senha...');

      // Obter parâmetros da URL
      const tokenHash = searchParams.get('token_hash');
      const type = searchParams.get('type');

      // Verificar se é primeiro acesso (parceiro já logado)
      if (type === 'primeiro_acesso' && user) {
        console.log('🔐 PRIMEIRO ACESSO: Atualizando senha do parceiro logado');
        
        const response = await fetch('/api/parceiros-auth/definir-senha-primeiro-acesso', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            user_id: user.id,
            senha,
            termos_aceitos: true
          })
        });

        if (!response.ok) {
          const error = await response.json();
          console.error('❌ Erro ao definir senha:', error);
          throw new Error(error.error || 'Erro ao definir senha');
        }

        const result = await response.json();
        const jaDefinida = result.already_defined === true;
        
        toast({
          title: jaDefinida ? "ℹ️ Senha já definida" : "🎉 Senha definida com sucesso!",
          description: jaDefinida
            ? "Você já possui senha definitiva. Redirecionando para o login."
            : (
                <div className="space-y-1">
                  <p>Senha atualizada com sucesso!</p>
                  <p className="text-sm">Você será redirecionado para fazer login com sua nova senha.</p>
                </div>
              ),
          duration: 5000,
        });

        // Limpar sessão localmente (signOut do Supabase pode retornar 403; sem sessão o usuário cai em /auth)
        console.log('🔐 Limpando sessão e redirecionando para login...');
        try {
          await supabase.auth.signOut({ scope: 'local' });
        } catch {
          // ignora 403 ou outro erro
        }
        clearSupabaseSessionFromStorage();
        const redirectMessage = jaDefinida
          ? 'Você já possui senha definitiva. Faça login com sua senha.'
          : 'Senha definida com sucesso! Faça login com sua nova senha.';
        sessionStorage.setItem(DEFINIR_SENHA_REDIRECT_KEY, JSON.stringify({
          message: redirectMessage,
          email: user.email ?? ''
        }));
        window.location.href = '/auth';
        return;
      }

      // Modo desenvolvimento: usar endpoint de teste
      if (process.env.NODE_ENV === 'development') {
        const tokenHash = searchParams.get('token_hash');
        if (!tokenHash) {
          throw new Error('Token não encontrado');
        }

        console.log('🔧 MODO DESENVOLVIMENTO: Ativando parceiro:', tokenHash);

        // Ativar parceiro diretamente em desenvolvimento
        const response = await fetch('/api/parceiros-auth/ativar-teste', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            parceiro_id: tokenHash,
            senha,
            termos_aceitos: true
          })
        });

        if (!response.ok) {
          const error = await response.json();
          console.error('❌ Erro na ativação:', error);
          throw new Error(error.error || 'Erro ao ativar parceiro');
        }

        const result = await response.json();
        console.log('✅ Parceiro ativado em modo teste:', result);

        toast({
          title: "🎉 Conta ativada com sucesso!",
          description: (
            <div className="space-y-1">
              <p>Parceiro <strong>{result.parceiro.nome}</strong> ativado!</p>
              <p className="text-sm"><strong>Email:</strong> {result.credenciais.email}</p>
              <p className="text-sm"><strong>Senha:</strong> {result.credenciais.senha}</p>
              <p className="text-xs text-gray-500">Agora você pode fazer login com essas credenciais.</p>
            </div>
          ),
          duration: 8000,
        });

        setTimeout(() => {
          navigate('/auth', { 
            state: { 
              message: 'Conta ativada com sucesso! Agora você pode fazer login.',
              email: result.credenciais.email || '',
              senha: result.credenciais.senha || ''
            }
          });
        }, 3000);

        return;
      }

      // Modo produção: fluxo normal
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        throw new Error('Sessão não encontrada');
      }

      // Chamar API para definir senha
      const response = await fetch('/api/parceiros-auth/definir-senha', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`
        },
        body: JSON.stringify({
          senha,
          confirmacao_senha: confirmacaoSenha,
          termos_aceitos: true
        })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Erro ao definir senha');
      }

      console.log('✅ Senha definida com sucesso');

      // Mostrar mensagem de sucesso
      toast({
        title: "🎉 Senha definida com sucesso!",
        description: "Você será redirecionado para fazer login.",
      });

      // Fazer logout local da sessão temporária (scope: 'local' evita 403)
      try {
        await supabase.auth.signOut({ scope: 'local' });
      } catch (err) {
        console.warn('Logout remoto ignorado:', err);
      }

      setTimeout(() => {
        navigate('/auth', { 
          state: { 
            message: 'Conta ativada com sucesso! Faça login com suas credenciais.',
            email: '' 
          }
        });
      }, 2000);

    } catch (error: any) {
      console.error('❌ Erro ao definir senha:', error);
      
      toast({
        title: "❌ Erro ao definir senha",
        description: error.message || "Tente novamente mais tarde.",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  }, [senha, confirmacaoSenha, searchParams, user, navigate, toast]);

  const handleDefinirSenha = (e: React.FormEvent) => {
    e.preventDefault();

    if (!senhaValida()) {
      toast({
        title: "⚠️ Senha inválida",
        description: "Por favor, atenda todos os requisitos de senha.",
        variant: "destructive"
      });
      return;
    }

    setShowTermosModal(true);
  };

  const layoutComFundo = (children: React.ReactNode) => (
    <div className="flex items-center justify-center min-h-screen p-4 relative overflow-hidden">
      {/* Imagem mobile */}
      <div
        className="fixed inset-0 -z-10 sm:hidden"
        style={{
          backgroundImage: `url(/image_3_mobile.jpeg)`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundRepeat: "no-repeat",
        }}
      />
      {/* Imagem desktop */}
      <div
        className="fixed inset-0 -z-10 hidden sm:block"
        style={{
          backgroundImage: `url(/engineer-solar-rooftop_2.jpeg)`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundRepeat: "no-repeat",
        }}
      />
      <div className="fixed inset-0 -z-10 bg-sun7-blue-light/60 backdrop-blur-[1px]" />
      <div className="relative z-10 w-full max-w-md">{children}</div>
    </div>
  );

  // Renderização: Verificando token
  if (verificandoToken) {
    return layoutComFundo(
      <Card className="w-full shadow-lg bg-sun7-white/95 backdrop-blur-sm">
          <CardContent className="pt-6 text-center">
            <Loader2 className="w-12 h-12 mx-auto mb-4 animate-spin text-blue-600" />
            <p className="text-lg font-medium text-gray-700">Verificando link de ativação...</p>
            <p className="text-sm text-gray-500 mt-2">Por favor, aguarde</p>
          </CardContent>
        </Card>
    );
  }

  // Renderização: Token inválido
  if (!tokenValido) {
    return layoutComFundo(
      <Card className="w-full shadow-lg bg-sun7-white/95 backdrop-blur-sm">
          <CardHeader className="text-center">
            <XCircle className="w-16 h-16 mx-auto mb-4 text-red-500" />
            <CardTitle className="text-2xl">Link Inválido ou Expirado</CardTitle>
            <CardDescription className="text-base mt-2">
              O link de ativação não é mais válido. Isso pode acontecer se:
            </CardDescription>
          </CardHeader>
          <CardContent>
            <ul className="list-disc list-inside space-y-2 text-sm text-gray-600 mb-6">
              <li>O link já foi usado anteriormente</li>
              <li>O link expirou (válido por 24 horas)</li>
              <li>O link foi digitado incorretamente</li>
            </ul>
            <div className="space-y-3">
              <Button 
                onClick={() => navigate('/auth')} 
                className="w-full"
                variant="default"
              >
                Ir para Login
              </Button>
              <Button 
                onClick={() => navigate('/contato')} 
                className="w-full"
                variant="outline"
              >
                Entrar em Contato
              </Button>
            </div>
          </CardContent>
        </Card>
    );
  }

  // Renderização: Formulário de definição de senha
  return layoutComFundo(
    <Card className="w-full shadow-xl bg-sun7-white/95 backdrop-blur-sm">
        <CardHeader className="text-center pb-4">
          <div className="w-16 h-16 mx-auto mb-4 bg-blue-100 rounded-full flex items-center justify-center">
            <CheckCircle2 className="w-10 h-10 text-blue-600" />
          </div>
          <CardTitle className="text-2xl">Defina sua Senha</CardTitle>
          <CardDescription className="text-base mt-2">
            {nomeUsuario ? `Olá, ${nomeUsuario}! ` : 'Olá! '}
            Crie sua senha de acesso à plataforma Sun7 Link
          </CardDescription>
        </CardHeader>
        
        <CardContent>
          <form onSubmit={handleDefinirSenha} className="space-y-5">
            {/* Campo: Nova Senha */}
            <div>
              <label htmlFor="senha" className="block text-sm font-medium mb-2 text-gray-700">
                Nova Senha *
              </label>
              <div className="relative">
                <Input
                  id="senha"
                  type={mostrarSenha ? 'text' : 'password'}
                  value={senha}
                  onChange={(e) => setSenha(e.target.value)}
                  placeholder="Digite sua senha"
                  disabled={loading}
                  className="pr-10"
                  autoComplete="new-password"
                />
                <button
                  type="button"
                  onClick={() => setMostrarSenha(!mostrarSenha)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                  tabIndex={-1}
                >
                  {mostrarSenha ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>

            {/* Campo: Confirmar Senha */}
            <div>
              <label htmlFor="confirmacao" className="block text-sm font-medium mb-2 text-gray-700">
                Confirmar Senha *
              </label>
              <div className="relative">
                <Input
                  id="confirmacao"
                  type={mostrarConfirmacao ? 'text' : 'password'}
                  value={confirmacaoSenha}
                  onChange={(e) => setConfirmacaoSenha(e.target.value)}
                  placeholder="Digite a senha novamente"
                  disabled={loading}
                  className="pr-10"
                  autoComplete="new-password"
                />
                <button
                  type="button"
                  onClick={() => setMostrarConfirmacao(!mostrarConfirmacao)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                  tabIndex={-1}
                >
                  {mostrarConfirmacao ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>

            {/* Indicadores de Validação */}
            <div className="bg-gray-50 p-4 rounded-lg border border-gray-200 space-y-2">
              <p className="text-sm font-semibold text-gray-700 mb-3">
                📋 Requisitos da Senha:
              </p>
              
              <div className="space-y-2">
                <ValidacaoItem 
                  valido={validacoes.tamanho} 
                  texto="Mínimo 6 caracteres" 
                />
                <ValidacaoItem 
                  valido={validacoes.temLetra} 
                  texto="Pelo menos uma letra" 
                />
                <ValidacaoItem 
                  valido={validacoes.temNumero} 
                  texto="Pelo menos um número" 
                />
                <ValidacaoItem 
                  valido={validacoes.senhasIguais} 
                  texto="Senhas coincidem" 
                />
              </div>
            </div>

            {/* Botão de Submissão */}
            <Button 
              type="submit" 
              className="w-full h-11 text-base font-medium" 
              disabled={loading || !senhaValida()}
            >
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Definindo senha...
                </>
              ) : (
                <>
                  <CheckCircle2 className="mr-2 h-4 w-4" />
                  Definir Senha e Ativar Conta
                </>
              )}
            </Button>
          </form>

          <TermosAceiteModal
            open={showTermosModal}
            onOpenChange={setShowTermosModal}
            onAccept={executarDefinirSenha}
            loading={loading}
          />
        </CardContent>
      </Card>
  );
}

/**
 * Componente auxiliar: Item de validação
 */
interface ValidacaoItemProps {
  valido: boolean;
  texto: string;
}

function ValidacaoItem({ valido, texto }: ValidacaoItemProps) {
  return (
    <div className="flex items-center gap-2">
      {valido ? (
        <CheckCircle2 className="w-4 h-4 text-green-600 flex-shrink-0" />
      ) : (
        <XCircle className="w-4 h-4 text-gray-400 flex-shrink-0" />
      )}
      <span className={`text-sm ${valido ? 'text-green-700 font-medium' : 'text-gray-600'}`}>
        {texto}
      </span>
    </div>
  );
}
